import { queryOptions } from '@tanstack/react-query'
import { ApiError, apiExchange, apiGet, apiPostJson } from './client'
import { saveBytes } from './inventoree'

/**
 * ADM -> Map, calea noua. Perechile vechi, din
 * `application/controllers/Adm.php`:
 *
 *   map()            -> GET  /adm/map
 *   mapData()        -> GET  /adm/map/data
 *   mapHealth()      -> POST /adm/map/health
 *   exportMapExcel() -> GET  /adm/map/export.xlsx
 *   exportMapPdf()   -> POST /adm/map/export.pdf
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`.
 *
 * DOUA USI CATRE ACELASI ECRAN. `adm/map` e o intrare in meniul Softsee -
 * parcul intreg, fara sa se ceara nimic dinainte. `adm/map?root=<id>` vine de
 * pe fisa unui lucru: de pe elementele afectate ale unui tichet in Easeedesk,
 * de pe fisa unui element si fila lui de impact in registru, si de pe
 * compozitia si dependentele unui serviciu din catalog. Adresele sunt cele din
 * portal, neschimbate.
 */

/** Un element din „Start from". */
export interface MapOption {
  id: number
  name: string
  className: string
  kind: number
}

export interface MapScreen {
  pageTitle: string
  pageHeader: string
  breadcrumbs: string[]
  /** Cele trei vederi, de pe server, ca lista din bara sa nu se desparta de
      felurile de relatie pe care le filtreaza. */
  views: string[]
  services: MapOption[]
  roots: MapOption[]
  root: number
  rootName: string
  nodeBudget: number
  maxDepth: number
  /** Serverul asta chiar poate scoate un PDF? Butonul se deseneaza doar atunci. */
  pdfAvailable: boolean
}

export interface MapNode {
  id: number
  label: string
  class: string
  /** 1 aparat, 2 aplicatie, 3 serviciu IT, 4 serviciu de afaceri. */
  kind: number
  status: number
  /** `null` cand nimeni n-a pus o treapta - si atunci nodul e neutru, nu verde. */
  criticality: number | null
  company: number
  deviceId: number | null
  lastSeen: string
  /** Numai pentru coloana din foaia de calcul - pe desen nu incape. */
  companyName: string
  /** 1 operational, 2 degradat, 3 cazut. Lipsa inseamna operational. */
  health: number
  /** Subtipul aparatului (Firewall, Switch, NAS...) - pentru asezarea pe niveluri. */
  subtype?: string
  /** Rolurile puse pe CI / aparat (`database_server`, `virtualization_host`...). */
  roles?: string[]
  /** Rolul intr-o aplicatie de business: 1 baza de date, 2 aplicatie, 3 web, 4 statie client; 0 = niciunul. */
  appRole?: number
  /*
   * UNDE STA NODUL - pentru casetele si filtrele hartii (`map/grouping.ts`).
   * Optionale: un server mai vechi nu le trimite, si atunci nodul sta in afara
   * casetelor si trece de filtrele de rol.
   */
  /** Sediul aparatului; null pentru ce nu e aparat sau n-are sediu. */
  officeId?: number | null
  officeName?: string
  /** Prima adresa buna a aparatului, IPv4 inainte de IPv6. */
  ip?: string
  /** Reteaua de sediu (`it_office_networks`) care contine adresa, ca CIDR; '' = niciuna. */
  network?: string
  networkName?: string
  /** workstation | peripheral | server | network | other; '' = nu e aparat. */
  role?: string
  /** Aplicatiile de business, cea mai critica intai (criticitate 1-4, 4 = critica). */
  apps?: MapNodeApp[]
}

export interface MapNodeApp {
  id: number
  name: string
  criticality: number
}

export interface MapEdge {
  id: number
  source: number
  target: number
  type: string
  label: string
  origin: number
  confidence: number
  observations: number
  /** 1 activa, 0 a incetat sa mai apara. */
  status: number
  lastSeen: string
  /** Numai pe muchiile de retea, si numai cand fluxurile stiu portul. */
  port?: string
  portCertain?: boolean
}

export interface MapRequest {
  root: number
  view: string
  depth: number
  direction: string
  min_confidence: number
  min_observations: number
  origin: number
  seen_since: string
  show_gone: number
}

export interface MapData {
  nodes: MapNode[]
  edges: MapEdge[]
  /** Mersul s-a oprit la bugetul serverului: desenul e o parte, nu tot. */
  truncated: boolean
  request: MapRequest
}

/** Ce trimite bara de filtre. Ce nu e scris aici, serverul pune implicit. */
export interface MapQuery {
  root?: number
  view?: string
  depth?: number
  direction?: string
  min_confidence?: number
  show_gone?: number
}

export function mapQueryString(query: MapQuery): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '' && value !== 0) params.set(key, String(value))
  }
  const text = params.toString()
  return text === '' ? '' : `?${text}`
}

export function mapScreenQuery(root: number) {
  return queryOptions({
    queryKey: ['adm', 'map', 'screen', root],
    queryFn: () => apiGet<MapScreen>(`/adm/map${root > 0 ? `?root=${root}` : ''}`),
  })
}

export function mapDataQuery(query: MapQuery) {
  return queryOptions({
    queryKey: ['adm', 'map', 'data', query],
    queryFn: () => apiGet<MapData>(`/adm/map/data${mapQueryString(query)}`),
  })
}

/**
 * Numai starile, cerute din minut in minut.
 *
 * Prin POST, ca in portal: o harta plina inseamna trei sute de numere, iar un
 * GET cu ele in coada trece de limita de lungime a adresei si cade cu 414 -
 * tacut, o data la cateva ecrane.
 */
export function fetchMapHealth(ids: number[]): Promise<{ health: Record<string, number> }> {
  return apiPostJson<{ health: Record<string, number> }>('/adm/map/health', { ids })
}

/**
 * CE E PE HARTA, CA FOAIE DE CALCUL.
 *
 * Aceiasi parametri ca desenul, in coada adresei: fisierul tine exact graful
 * care se vedea, cu aceleasi filtre si aceeasi taietura - si taietura se si
 * scrie in el, fiindca un xlsx trimis mai departe n-are cum s-o spuna singur.
 */
export async function downloadMapXlsx(query: MapQuery): Promise<void> {
  const out = await apiExchange(`/adm/map/export.xlsx${mapQueryString(query)}`, { method: 'GET' })
  saveBytes(out.bytes, out.type, out.filename, 'adm-map.xlsx')
}

/**
 * HARTA CA DOCUMENT.
 *
 * Poza pleaca de aici, nu de pe server: asezarea nodurilor o calculeaza
 * cytoscape la fiecare incarcare, si serverul n-are cum s-o refaca. El o
 * verifica si o pune in pagina, cu antetul care spune ce se vedea si cand.
 *
 * Cand serverul n-are cu ce s-o scoata raspunde 501 cu o propozitie, si aia se
 * arata - nu se salveaza un fisier care nu e un PDF.
 */
export async function downloadMapPdf(png: string, summary: string, partial: boolean): Promise<void> {
  const out = await apiExchange('/adm/map/export.pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ png, summary, partial: partial ? 1 : 0 }),
  })

  if (!out.type.includes('pdf')) {
    let message = 'The map PDF could not be produced.'
    try {
      message = (JSON.parse(out.text) as { message?: string }).message ?? message
    } catch {
      /* Raspunsul nu era JSON; ramane propozitia de mai sus. */
    }
    throw new ApiError(out.status || 501, message)
  }

  saveBytes(out.bytes, out.type, out.filename, 'adm-map.pdf')
}
